import { type Canvas, Textbox } from "fabric";
import { TEXT_LAYER_TYPE } from "../constants.ts";
import type {
  CanvasSize,
  TextLayer,
  TextLayerProperties,
  TextLayerSettings,
} from "../types.ts";
import { createLayer, getLayers } from "./layer.ts";
import { updateLayerShadowStyle } from "./shadow.ts";
import { updateLayerStrokeStyle } from "./stroke.ts";

export const createTextLayer = ({
  fabricCanvas,
  canvasSize,
  name,
  positionIndex,
  properties,
}: {
  fabricCanvas: Canvas;
  canvasSize: CanvasSize;
  name?: string;
  positionIndex?: number;
  properties: TextLayerProperties;
}) => {
  const textBox = new Textbox(properties.content ?? "Click to edit text", {
    left: properties.left ?? canvasSize.width / 2,
    top: properties.top ?? canvasSize.height / 2,
    width: properties.width ?? 300,
    fontFamily: properties.fontFamily ?? "Arial",
    fontSize: properties.fontSize ?? 40,
    fill: properties.textColor ?? "rgba(0, 0, 0, 1)",
    textAlign: properties.textAlign ?? "left",
    fontWeight: properties.isBold ? "bold" : "normal",
    fontStyle: properties.isItalic ? "italic" : "normal",
    underline: properties.isUnderline ?? false,
    linethrough: properties.isStrikethrough ?? false,
    editable: true,
    splitByGrapheme: false,
    originX: "center",
    originY: "center",
    angle: properties.rotation ?? 0,
    lineHeight: properties.lineHeight ?? 1.2,
    charSpacing: properties.charSpacing ?? 0,
    styles: properties.styles,
    stroke: properties.stroke ?? "rgba(0, 0, 0, 1)",
    strokeWidth: properties.strokeWidth ?? 0,
  }) as TextLayer;

  lockTextLayerScaling(textBox);

  updateLayerShadowStyle(
    textBox,
    properties.shadowStyle ?? "none",
    properties.shadowDirection ?? "top",
    properties.shadowDistance ?? "small",
  );
  updateLayerStrokeStyle(textBox, properties.strokeStyle ?? "solid");

  const currentLayerCount = getLayers(fabricCanvas).length;

  return createLayer({
    fabricCanvas,
    layer: textBox,
    name: name ?? `Text ${currentLayerCount + 1}`,
    positionIndex: positionIndex ?? currentLayerCount,
    layerType: TEXT_LAYER_TYPE,
  });
};

export const lockTextLayerScaling = (textLayer: TextLayer) => {
  textLayer.set({
    lockScalingX: true,
    lockScalingY: true,
  });

  // XXX: We manually override the cursor style for the text layer to indicate that text box width can be adjusted.
  //  This is a workaround because the text box is locked in scaling and we cannot use the default cursor style.
  const overrideCursor = () => "ew-resize";
  textLayer.controls["ml"].cursorStyleHandler = overrideCursor;
  textLayer.controls["mr"].cursorStyleHandler = overrideCursor;
};

export const duplicateTextLayer = (
  fabricCanvas: Canvas,
  canvasSize: CanvasSize,
  textLayer: TextLayer,
) =>
  createTextLayer({
    fabricCanvas,
    canvasSize,
    properties: {
      content: textLayer.text,
      left: textLayer.left,
      top: textLayer.top,
      textColor: textLayer.fill as string,
      fontFamily: textLayer.fontFamily,
      fontSize: textLayer.fontSize,
      textAlign: textLayer.textAlign,
      isBold: textLayer.fontWeight === "bold",
      isItalic: textLayer.fontStyle === "italic",
      isUnderline: textLayer.underline,
      isStrikethrough: textLayer.linethrough,
      rotation: textLayer.angle,
      width: textLayer.width,
      lineHeight: textLayer.lineHeight,
      charSpacing: textLayer.charSpacing,
      styles: textLayer.styles,
      shadowStyle: textLayer.shadowStyle,
      stroke: textLayer.stroke,
      strokeStyle: textLayer.strokeStyle,
      strokeWidth: textLayer.strokeWidth,
    },
  });

export const updateTextLayer = (
  textLayer: TextLayer,
  options: Partial<Required<TextLayerSettings>>,
) => {
  const fabricOptions = convertTextLayerOptionsToFabricOptions(options);
  textLayer.canvas?.fire("layer:text:changed", {
    layer: textLayer,
    changes: options,
  });
  if (
    options.shadowStyle !== undefined ||
    options.shadowDirection !== undefined ||
    options.shadowDistance !== undefined
  ) {
    updateLayerShadowStyle(
      textLayer,
      options.shadowStyle ?? textLayer.shadowStyle,
      options.shadowDirection ?? "top",
      options.shadowDistance ?? "small",
    );
  }
  if (options.strokeStyle !== undefined) {
    updateLayerStrokeStyle(textLayer, options.strokeStyle);
  }
  textLayer.set(fabricOptions);

  for (const property in fabricOptions) {
    for (const lineIndex in textLayer.styles) {
      for (const charIndex in textLayer.styles[lineIndex]) {
        const charStyle = textLayer.styles[lineIndex][charIndex] as any;
        if (charStyle[property] !== undefined) {
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete charStyle[property];
        }
      }
    }
  }
  textLayer.setCoords();
};

export const updateTextSelection = (
  textLayer: TextLayer,
  options: Partial<Required<TextLayerSettings>>,
) => {
  textLayer.canvas?.fire("layer:text:selection:changed", {
    layer: textLayer,
    changes: options,
  });

  const fabricOptions = convertTextLayerOptionsToFabricOptions(options);

  if (textLayer.selectionStart !== textLayer.selectionEnd) {
    textLayer.setSelectionStyles(fabricOptions);

    // XXX: We need to force the text layer to recalculate its dimensions + invalidate the cache so that the text layer selection styles are applied correctly,
    //  otherwise only styles impacting the text layer dimensions are applied.
    textLayer.dirty = true;
    textLayer._clearCache();
    textLayer.initDimensions();
  }

  textLayer.setCoords();
};

const convertTextLayerOptionsToFabricOptions = (
  options: Partial<Required<TextLayerSettings>>,
): Partial<TextLayer> => {
  const fabricOptions: Partial<TextLayer> = {};

  if (options.fontFamily !== undefined) {
    fabricOptions.fontFamily = options.fontFamily;
  }

  if (options.fontSize !== undefined) fabricOptions.fontSize = options.fontSize;

  if (options.textColor !== undefined) {
    fabricOptions.fill = options.textColor;
  }

  if (options.textAlign !== undefined) {
    fabricOptions.textAlign = options.textAlign;
  }

  if (options.isBold !== undefined) {
    fabricOptions.fontWeight = options.isBold ? "bold" : "normal";
  }

  if (options.isItalic !== undefined) {
    fabricOptions.fontStyle = options.isItalic ? "italic" : "normal";
  }

  if (options.isUnderline !== undefined) {
    fabricOptions.underline = options.isUnderline;
  }

  if (options.isStrikethrough !== undefined) {
    fabricOptions.linethrough = options.isStrikethrough;
  }

  if (options.rotation !== undefined) {
    fabricOptions.angle = options.rotation;
  }

  if (options.lineHeight !== undefined) {
    fabricOptions.lineHeight = options.lineHeight;
  }

  if (options.charSpacing !== undefined) {
    fabricOptions.charSpacing = options.charSpacing;
  }

  if (options.stroke !== undefined) {
    fabricOptions.stroke = options.stroke;
  }
  if (options.strokeWidth !== undefined) {
    fabricOptions.strokeWidth = options.strokeWidth;
  }

  return fabricOptions;
};
